import React, { useState } from 'react';
import { Menu, X, ShoppingBag, Heart, Search, User, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useCart } from '../contexts/CartContext';
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    user,
    isAuthenticated,
    logout
  } = useAuth();
  const {
    favoritesCount
  } = useFavorites();
  const {
    cartCount
  } = useCart();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/');
  };
  const handleCartClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate('/cart');
    }
  };
  const handleFavoritesClick = () => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate('/favorites');
    }
  };
  const navLinks = [{
    name: 'Balloons',
    href: '/balloons'
  }, {
    name: 'Flowers',
    href: '/flowers'
  }, {
    name: 'Gift Boxes',
    href: '/gift-boxes'
  }, {
    name: 'Occasions',
    href: '/occasions'
  }, {
    name: 'About',
    href: '/about'
  }];
  return <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-rose-100" data-id="element-677">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-678">
        <div className="flex justify-between items-center h-20" data-id="element-679">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center cursor-pointer gap-3" data-id="element-680">
            <img src="https://cdn.magicpatterns.com/uploads/6brmibkzkhHWCQaXX7VcV5/Blue_.png" alt="Bloom & Pop Logo" className="h-10 w-10 object-contain" data-id="element-681" />
            <span className="font-serif text-2xl font-bold text-rose-500" data-id="element-682">
              Bloom & Pop
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8" data-id="element-683">
            {navLinks.map(link => <Link key={link.name} to={link.href} className="text-gray-600 hover:text-rose-500 font-medium transition-colors" data-id="element-684">
                {link.name}
              </Link>)}
          </div>

          {/* Icons */}
          <div className="hidden md:flex items-center space-x-4" data-id="element-685">
            <button className="p-2 text-gray-500 hover:text-rose-500 transition-colors" data-id="element-686">
              <Search className="w-5 h-5" data-id="element-687" />
            </button>
            <button onClick={handleFavoritesClick} className="p-2 text-gray-500 hover:text-rose-500 transition-colors relative" data-id="element-688">
              <Heart className="w-5 h-5" data-id="element-689" />
              {favoritesCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center font-semibold" data-id="element-690">
                  {favoritesCount}
                </span>}
            </button>
            <Button variant="primary" size="sm" leftIcon={<ShoppingBag className="w-4 h-4" data-id="element-692" />} onClick={handleCartClick} data-id="element-691">
              Cart ({cartCount})
            </Button>

            {isAuthenticated ? <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-200" data-id="element-693">
                <Link to="/dashboard" data-id="element-694">
                  <Button variant="ghost" size="sm" leftIcon={<User className="w-4 h-4" data-id="element-696" />} data-id="element-695">
                    {user?.surname ? `${user.name} ${user.surname}` : user?.name}
                  </Button>
                </Link>
                <Button variant="ghost" size="sm" onClick={handleLogout} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50" data-id="element-697">
                  <LogOut className="w-4 h-4" data-id="element-698" />
                </Button>
              </div> : <div className="flex items-center space-x-2 ml-4 pl-4 border-l border-gray-200" data-id="element-699">
                <Link to="/login" data-id="element-700">
                  <Button variant="ghost" size="sm" data-id="element-701">
                    Login
                  </Button>
                </Link>
                <Link to="/signup" data-id="element-702">
                  <Button variant="outline" size="sm" data-id="element-703">
                    Sign Up
                  </Button>
                </Link>
              </div>}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center" data-id="element-704">
            <button onClick={() => setIsOpen(!isOpen)} className="p-2 rounded-md text-gray-600 hover:text-rose-500 focus:outline-none" data-id="element-705">
              {isOpen ? <X className="w-6 h-6" data-id="element-706" /> : <Menu className="w-6 h-6" data-id="element-707" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence data-id="element-708">
        {isOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-white border-b border-rose-100 overflow-hidden" data-id="element-709">
            <div className="px-4 pt-2 pb-6 space-y-2" data-id="element-710">
              {navLinks.map(link => <Link key={link.name} to={link.href} onClick={() => setIsOpen(false)} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-rose-500 hover:bg-rose-50" data-id="element-711">
                  {link.name}
                </Link>)}

              {/* Mobile Favorites Link */}
              <button onClick={() => {
            handleFavoritesClick();
            setIsOpen(false);
          }} className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-rose-500 hover:bg-rose-50 flex items-center justify-between" data-id="element-712">
                <span data-id="element-713">Favorites</span>
                {favoritesCount > 0 && <span className="w-6 h-6 bg-rose-500 text-white text-xs rounded-full flex items-center justify-center font-semibold" data-id="element-714">
                    {favoritesCount}
                  </span>}
              </button>

              <div className="pt-4 flex flex-col space-y-3 border-t border-gray-100 mt-4" data-id="element-715">
                {isAuthenticated ? <>
                    <Link to="/dashboard" onClick={() => setIsOpen(false)} data-id="element-716">
                      <Button variant="primary" className="w-full justify-center" leftIcon={<User className="w-4 h-4" data-id="element-718" />} data-id="element-717">
                        Dashboard
                      </Button>
                    </Link>
                    <Button variant="outline" className="w-full justify-center text-rose-500 border-rose-200" onClick={() => {
                handleLogout();
                setIsOpen(false);
              }} data-id="element-719">
                      Logout
                    </Button>
                  </> : <>
                    <Link to="/login" onClick={() => setIsOpen(false)} data-id="element-720">
                      <Button variant="ghost" className="w-full justify-center" data-id="element-721">
                        Login
                      </Button>
                    </Link>
                    <Link to="/signup" onClick={() => setIsOpen(false)} data-id="element-722">
                      <Button variant="primary" className="w-full justify-center" data-id="element-723">
                        Sign Up
                      </Button>
                    </Link>
                  </>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}